import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"

import { IdcardOutlined, ProfileOutlined } from "@ant-design/icons"
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Tabs,
  TabsProps,
  TimeRangePickerProps as AntDatePickerProps,
  Typography
} from "antd"
import dayjs from "dayjs"

import { DatePicker } from "@/components/_Shared/DatePicker"
import { SimpleModal, SimpleModalRef } from "@/components/_Shared/SimpleModal"
import { SvgImage } from "@/components/_Shared/SvgImage/SvgImage"
import {
  DeliveryAddressesAddForm,
  DeliveryAddressesAddFormProps
} from "@/components/DeliveryAddresses/DeliveryAddressesAddForm/DeliveryAddressesAddForm"
import { TransportTable } from "@/components/Transport/TransportTable"
import { Resource, Scope } from "@/constants/permission"
import { getCustomersByClient, getCustomersSelectList } from "@/http/customer"
import { getFuelForOrder } from "@/http/fuel"
import { getNotArchivedFuelTypesByDepot } from "@/http/fuelType"

import classes from "./OrderAddForm.module.scss"
import { BackdatingOrderAddForm } from "../BackdatingOrderAddForm/BackdatingOrderAddForm"
import { DeliveryForm, DeliveryFormRef } from "../DeliveryForm"
import { OrderDeliveryTable } from "../OrderDeliveryTable/OrderDeliveryTable"
import { OrderFuelTable } from "../OrderFuelTable"

import { useDepotSelectListQuery } from "@/hook/Dictionaries/useDepotQuery"
import { useDriverForOrderQuery } from "@/hook/Dictionaries/useDriverQuery"
import { useTransportForOrderQuery } from "@/hook/Dictionaries/useTransportQuery"
import { useOrderMutation } from "@/hook/Orders/useOrderMutation"
import { useOrderByIdQuery } from "@/hook/Orders/useOrderQuery"
import { useTimeUnloadFuelMutation } from "@/hook/TimeUnloadFuel/useTimeUnloadFuelMutation"
import { usePermission } from "@/hook/usePermission"

import { IShippingSchedule } from "@/types/IBulkOrder"
import { Customer, ICustomer, ICustomerSelectListItem } from "@/types/ICustomer"
import { IDelivery } from "@/types/IDelivery"
import { IDeliveryAddress } from "@/types/IDeliveryAddress"
import { IDriver } from "@/types/IDriver"
import { convertFuelAmountToLitres, convertFuelAmountToTonn, IFuelOrder } from "@/types/IFuel"
import { IFuelType } from "@/types/IFuelType"
import {
  DeliveryType,
  deliveryTypes,
  ExchangeType,
  exchangeTypes,
  IOrderCreate,
  IOrderFuel,
  ITranportPairOrder,
  OrderStatus
} from "@/types/IOrder"
import { ITimeUnloadFuel } from "@/types/ITimeUnloadFuel"
import { ITransport, ITransportClass } from "@/types/ITransport"

import { filterSelectOption } from "@/utils/filterSelectOption"
import { formatDate } from "@/utils/formatDate"
import { formatFuelVolume, getMoney } from "@/utils/formatNumber"
import { formatTransport } from "@/utils/formatString"


const { Text } = Typography
export interface OrderAddFormProps {
  open(
    customer?: ICustomer,
    customerClientId?: number,
    id?: number,
    isReserve?: boolean,
    depotId?: number,
    editByCustomer?: boolean,
    isDeliveryOrder?: boolean,
    isFullUpdate?: boolean,
    fuelTypeId?: number,
    chosenDate?: Date,
    cloneId?: number,
    shippingSchedule?: IShippingSchedule
  ): void
  close(): void
}

export interface OrderAddFormValues {
  date: Date
  customerId: number
  deliveryType: DeliveryType
  exchangeType?: ExchangeType
  wishPeriodTime?: string
  isReserve: boolean
  transports: TransportAddFormValues[]
  fuelQuotes: FuelQuoteAddFormValues[]
  deliveryAddressId?: number
  deliveryCost?: number
  pump?: boolean
  deliveryCommentary?: string
  depotId: number
  fuelTypeId: number
  amount: number
  price: number
  isFuture: boolean
  withPayment: boolean,
  isCustomDeliveryCost: boolean,
  customDeliveryCost: number,

  wishTimeSlotId: number,
  wishTimeSlot: string,
  driverId?: number | undefined,
  truckId?: number | undefined,
  pickupId?: number | undefined,
  fuelAmoutTransport: number,
  density: number,
  volume: number,
  weight: number
}

export interface FuelQuoteAddFormValues {
  id: number
  quoteId: number
  amount: number
  fuelTypeId: number
  fuelType: string
  density: number
  price: number
  addon: number,
  isFuture: boolean,
  limitSettingId: number
}

export interface TransportAddFormValues {
  driverId: number
  truckId: number
  pickupId?: number
  wishTimeSlot?: string
  fuelAmount: number
}

export interface OrderData {
  isReserve: boolean
  date: string
  customer: CustomerData
  wishPeriodTime?: string
  deliveryType: string
  delivery?: IDelivery
  exchangeType?: string
  driver?: IDriver
  truck?: ITransport
  pickup?: ITransport
  depot: string
  depotId: number
  fuels: IOrderFuel[]
  transports: ITranportPairOrder[]
  deliveryPrice: number
  fullPrice: number
  finalPriceText: string
  /**
   * Obsolete.
   * Used only for bulk orders
   */
  isFuture?: boolean
}

export interface CustomerData {
  id: number
  name: string
}

enum InputValueOrWeight{
  volume,
  weight
}

export const OrderAddForm = forwardRef<OrderAddFormProps>((props, ref) => {
  const [form] = Form.useForm<OrderAddFormValues>()
  const [id, setId] = useState<number>(0)
  const [cloneId, setCloneId] = useState<number>(0)
  const [shippingShedule, setShippingShedule] = useState<IShippingSchedule | undefined>(undefined)
  const [isEditByCustomer, setIsEditByCustomer] = useState<boolean>(false)
  const [hasWishTime, setHasWishTime] = useState<boolean>(false) 
  const [isCustomerOrder, setIsCustomerOrder] = useState<boolean>(true)
  const [customers, setCustomers] = useState<ICustomerSelectListItem[]>([])
  const [customer, setCustomer] = useState<ICustomer>()
  const [isReserve, setIsReserve] = useState<boolean>(false)
  const [isPickup, setIsPickup] = useState<boolean>(false)
  const [isDelivery, setIsDelivery] = useState<boolean>(false)
  const [isFullUpdate, setIsFullUpdate] = useState<boolean>(false)
  const [isBulkOrder, setIsBulkOrder] = useState<boolean>(false)
  const [isChangedOrderConsist, setIsChangedOrderConsist] = useState<boolean>(false) // был ли изменен состав заказа (топливо, НБ, дата и тд.)
  const [isPlanned, setIsPlanned] = useState<boolean>(false)
  const [isOpen, setIsOpen] = useState(false)
  const [activeKey, setActiveKey] = useState("1")
  const [isProcessing, setIsProcessing] = useState(false)
  const [isDriverChosen, setIsDriverChosen] = useState(false)
  const [isTruckChosen, setIsTruckChosen] = useState(false)
  const [isTimeUnloadChosen, setIsTimeUnloadChosen] = useState(false)
  const [orderDate, setOrderDate] = useState<Date | undefined>(undefined)
  const [ deliveryAddres, setDeliveryAddres ] = useState<IDeliveryAddress | undefined>(undefined)

  const [fuelTypes, setFuelTypes] = useState<IFuelType[]>()
  const [timeUnloadFuels, setTimeUnloadFuels] = useState<ITimeUnloadFuel[]>([])
  const [chosenTimeUnloadFuel, setChosenTimeUnloadFuel] = useState<ITimeUnloadFuel | undefined>(undefined)
  const [isFuelTypesLoaded, setIsFuelTypesLoaded] = useState(false)
  const [fuel, setFuel] = useState<IFuelOrder>()
  const [isFuelLoaded, setIsFuelLoaded] = useState<boolean>(false)
  const [amountFuel, setAmountFuel] = useState<number>(0)
  const [cannotAddFuel, setCannotAddFuel] = useState(true)
  const [chosenTransports, setChosenTransports] = useState<
    ITranportPairOrder[]
  >([])
  const [isTransportSelect, setIsTransportSelect] = useState<boolean>(false)
  const [chosenDepot, setChosenDepot] = useState<string>()
  const [isFuelSelect, setIsFuelSelect] = useState<boolean>(false)
  const [chosenFuels, setChosenFuels] = useState<IOrderFuel[]>([])
  const [price, setPrice] = useState(0)
  const [fullPrice, setFullPrice] = useState(0)
  const [isOrderOnClientApprovingTransportStatus, setIsOrderOnClientApprovingTransportStatus] = useState<boolean>(false)

  const [isFuelsLoaded, setIsFuelsLoaded] = useState<boolean>(false)
  const [chosenDeliveryType, setChosenDeliveryType] = useState<DeliveryType>(DeliveryType.Pickup)
  const [totalAmount, setTotalAmount] = useState<number>(0)

  const [newDrivers, setNewDrivers] = useState<IDriver[]>([])
  const [newTransports, setNewTransports] = useState<ITransport[]>([])
  const [isAddedNewDriver, setIsAddedNewDriver] = useState<boolean>(false)
  const [isAddedNewTransport, setIsAddedNewTransport] = useState<boolean>(false)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState("")
  const [modalText, setModalText] = useState("")
  const [isAddFuelProcess, setIsAddFuelProcess] = useState<boolean>(false)
  
  const { hasPermission } = usePermission()
  const isAdminOrManager = useMemo(() => {
    return hasPermission(Resource.OrderReport, Scope.Read)
  }, [hasPermission])

  const [data, setData] = useState<OrderAddFormValues>()
  const [orderData, setOrderData] = useState<OrderData>()

  const { data: depots, isLoading: depotsIsLoading } = useDepotSelectListQuery()
  const { data: drivers, isLoading: driversIsLoading } =
    useDriverForOrderQuery(id, isAdminOrManager ? undefined : customer?.id)
  const { data: transports, isLoading: transportsIsLoading } =
    useTransportForOrderQuery(id, isAdminOrManager ? undefined : customer?.id)
  const { data: order } = useOrderByIdQuery(id)
  const { data: orderClone } = useOrderByIdQuery(cloneId)

  const [messageApi, contextHolder] = message.useMessage()
  const simpleModalComponent = useRef<SimpleModalRef>(null)
  const addressComponent = useRef<DeliveryAddressesAddFormProps>(null)
  const deliveryForm = useRef<DeliveryFormRef>(null)

  const { createMutation, updateMutation, updateTransportForOrderMutation, recreateMutation } = useOrderMutation()
  const { getAvailableTimeUnloadFuelsMutation } = useTimeUnloadFuelMutation()
  const [notificationApi, notificationContextHolder] = notification.useNotification()

  useImperativeHandle(ref, () => ({
    open(
      customer?: ICustomer,
      customerClientId?: number,
      id?: number,
      isReserve?: boolean,
      depotId?: number,
      isEditByCustomer?: boolean,
      isDeliveryOrder?: boolean,
      isFullUpdate?: boolean,
      fuelTypeId?: number,
      chosenDate?: Date,
      cloneId?: number,
      shippingScheduleId?: IShippingSchedule
    ) {
      onOpenHandler(customer, customerClientId, id, isReserve, depotId, isEditByCustomer, isDeliveryOrder, isFullUpdate, fuelTypeId, chosenDate, cloneId, shippingScheduleId)
    },
    close() {
      onCloseHandler()
    }
  }))

  useEffect(() => {
    if (order && isOpen) {
      const response = order.response

      setChosenDepot(response.depot.name)

      form.setFields([
        { name: "date", value: dayjs(response.date) },
        { name: "deliveryType", value: response.deliveryType },
        { name: "exchangeType", value: response.exchangeType },
        { name: "customerId", value: response.customer.id },
        { name: "deliveryCost", value: response.deliveryPrice },
        { name: "deliveryAddressId", value: response?.delivery?.deliveryAddress?.id },
        { name: "wishTimeSlot", value: response.wishTimeSlot },
        { name: "depotId", value: response.depot.id },
        { name: "isReserve", value: false },
        { name: "transports", value: response.transports },
        { name: "fuelQuotes", value: response.fuelQuotes },
        { name: "deliveryCommentary", value: response.delivery?.commentary ?? "-" }
      ])

      if (isEditByCustomer) {
        uploadFuelTypes(response.depot.id)
        if (!isDelivery && hasWishTime) {
          timeUnloadFuelLoad()
        }
      }

      if (response.date) {
        onDateChange(undefined, response.date)
      }

      if (response.status == OrderStatus.OnClientApprovingTransport){
        setIsOrderOnClientApprovingTransportStatus(true)
      }

      if (response.deliveryType == DeliveryType.Delivery) {
        setIsDelivery(true)
        form.setFieldValue("deliveryCommentary", response.delivery?.commentary ?? "-")
      } 
      else if (response.deliveryType == DeliveryType.Pickup)  {
        setIsReserve(true)
      }
      else if (response.status == OrderStatus.NotSettedTime) {
        setIsEditByCustomer(false)
      }

      if (order.response.fuelQuotes && order.response.fuelQuotes.length > 0) {
        setIsFuelSelect(true)
      }
      
      if (order.response.transports && order.response.transports.length > 0) {
        setIsTransportSelect(true)
      }

      refershChosenFuels(response.fuelQuotes)
      refreshChosenTransports(response.transports ?? [])
      setChosenDeliveryType(response.deliveryType)
      setHasWishTime(response.depot.hasWishTime)

      const fuelQuotes: FuelQuoteAddFormValues[] = form.getFieldValue("fuelQuotes") ?? []
      setFullPrice(fuelQuotes.reduce((sum, quote) => sum + (quote.price * quote.amount), 0))

      if(response.bulkOrderId){
        setIsBulkOrder(true)
      }
    }
  }, [id, order, form, isOpen, drivers, transports])

  useEffect(()=>{
    if(isBulkOrder){
      let amount = order?.response.fuelQuotes[0].amount
      if(amount && amountFuel > amount){
        form.setFieldValue("weight", amount)
        onWeightOrVolumeChange(InputValueOrWeight.weight)
      }
    }
  }, [amountFuel, isBulkOrder])

  useEffect(() => {
    
    if (orderClone && isOpen) {      
      const response = orderClone.response
      setChosenDepot(response.depot.name)

      form.setFields([        
        { name: "deliveryType", value: response.deliveryType },
        { name: "exchangeType", value: response.exchangeType },
        { name: "customerId", value: response.customer.id },
        { name: "deliveryCost", value: response.deliveryPrice },
        { name: "deliveryAddressId", value: response?.delivery?.deliveryAddress?.id },
        { name: "wishTimeSlot", value: response.wishTimeSlot },
        { name: "depotId", value: response.depot.id },
        { name: "isReserve", value: false },
        { name: "transports", value: response.transports },
        { name: "deliveryCommentary", value: response.delivery?.commentary ?? "-" }
      ])
      
      onDepotChange(true)
      if (response.fuelQuotes[0].fuelTypeId) {
        form.setFieldValue("fuelTypeId", response.fuelQuotes[0].fuelTypeId)        
        fuelTypeChange()
      }

      if (response.deliveryType === DeliveryType.Delivery) {
        setIsDelivery(true)
        form.setFieldValue("deliveryCommentary", response.delivery?.commentary ?? "-")
      } 
      else if (response.deliveryType === DeliveryType.Pickup)  {
        setIsPickup(true)
      }
      
      if (!response.depot.hasWishTime) {
        if (orderClone.response.transports && orderClone.response.transports.length > 0) {
          setIsTransportSelect(true)
        }

        refreshChosenTransports(response.transports ?? [])
      } else {
        if (orderClone.response.transports && orderClone.response.transports.length > 0) {          
          form.setFieldValue("driverId", orderClone.response.transports[0].driverId)
          form.setFieldValue("truckId", orderClone.response.transports[0].truckId)
          form.setFieldValue("pickupId", orderClone.response.transports[0].pickupId)
          form.setFieldValue("transports", undefined)
          setIsDriverChosen(true)
          setIsTruckChosen(true)
        }
      }
      
      setChosenDeliveryType(response.deliveryType)
      setHasWishTime(response.depot.hasWishTime)
      refershChosenFuels(response.fuelQuotes)
    }
  }, [cloneId, orderClone, form, isOpen, drivers, transports])
 

  const title = useMemo(() => {
    if (id) {
      return "Редактировать"
    }
    if (isPlanned) {
      return "Забронировать"
    }
    if (isReserve) {
      return "Забронировать"
    }

    return "Заказать"
  }, [id, isReserve, isPlanned])

  const formOrderText = useMemo(() => {
    return id == 0 ? "Сформировать заказ" : "Изменить заказ"
  }, [id])

  const orderText = useMemo(() => {
    if (id) return "Подтвердить изменения"

    return "Создать заказ"
  }, [id])

  const allDrivers: IDriver[] = useMemo(() => {
    return [...(drivers?.response ?? []), ...(newDrivers ?? [])]
  }, [drivers, isAddedNewDriver])

  const allTrucks: ITransport[] = useMemo(() => {
    return [
      ...(transports?.response.filter(
        (t) => (t.class === ITransportClass.Truck) || (t.class === ITransportClass.TankerTruck)
      ) ?? []),
      ...(newTransports.filter((t) => (t.class === ITransportClass.Truck) || (t.class === ITransportClass.TankerTruck)) ?? [])
    ]
  }, [transports, isAddedNewTransport])

  const allPickups: ITransport[] = useMemo(() => {
    return [
      ...(transports?.response.filter(
        (t) => t.class === ITransportClass.Pickup
      ) ?? []),
      ...(newTransports.filter((t) => t.class === ITransportClass.Pickup) ?? [])
    ]
  }, [transports, isAddedNewTransport])

  const fuelPrice: number = useMemo(()=>{
      let price = 0
      if(isBulkOrder){
        price =  order?.response.fuelQuotes[0].price ?? 0
      }
      else if(fuel?.price && fuel.price > 0){
        price = fuel.price
      }
      else if(fuel?.lastSetPrice){
        price = fuel.lastSetPrice
      }

      return price
  }, [fuel, isBulkOrder])

  const onOpenHandler = (
    customer?: ICustomer,
    customerClientId?: number,
    id?: number,
    isReserve?: boolean,
    depotId?: number,
    isEditByCustomer?: boolean,
    isDeliveryOrder?: boolean,
    isFullUpdate?: boolean,
    fuelTypeId?: number,
    chosenDate?: Date,
    cloneId?: number,
    shippingSchedule?: IShippingSchedule
  ) => {
    if (id) {
      setId(id)
    }

     if (cloneId) {
      setCloneId(cloneId)
    }

    if(shippingSchedule){
      setShippingShedule(shippingSchedule)
    }

    form.resetFields()
    setTotalAmount(0)

    if (customer) {
      setCustomers([])
      form.setFieldValue("customerId", customer.id)
      setCustomer(customer)
      setIsCustomerOrder(true)
    } else if (customerClientId) {
      form.setFieldValue("customerId", customerClientId)
      getCustomersByClient({}, customerClientId).then((response) => {
        setCustomers(response?.response.data.map(c => ({
          value: c.id,
          label: c.name,
          customerClientId: c.customerClientId,
          customerClientName: c.customerClientName
        })))
        setIsCustomerOrder(false)
      })
    } else {
      getCustomersSelectList().then((response) => {
        setCustomers(response?.response ?? [])
        setIsCustomerOrder(false)
      })
    }

    if (id) {
      if (isReserve) {
        setIsReserve(isReserve)
      }
      if (isDeliveryOrder) {
        setIsDelivery(isDeliveryOrder)
      } else {
        setIsPickup(true)
      }
    }

    if (isEditByCustomer) {
      setIsEditByCustomer(isEditByCustomer)
    }

    if (isFullUpdate) {
      setIsFullUpdate(isFullUpdate)
    }

    if (chosenDate) {
      form.setFieldValue("date", dayjs(chosenDate))
      onDateChange(undefined, "")
    }

    if (depotId) {
      form.setFieldValue("depotId", depotId)
      onDepotChange(!!fuelTypeId)
    }

    if (fuelTypeId) {
      form.setFieldValue("fuelTypeId", fuelTypeId)
      fuelTypeChange()
    }

    setIsFuelsLoaded(false)
    setIsOpen(true)
  }

  const onCustomerChange = (customerId: number) => {
    setCustomer(new Customer(customers.find(c => c.value == customerId)))

    refreshChosenTransports([])
    form.setFieldValue("transports", [])
    form.setFieldValue("driverId", undefined)
    form.setFieldValue("truckId", undefined)
    form.setFieldValue("pickupId", undefined)
    setIsDriverChosen(false)
    setIsTruckChosen(false)
    setIsTimeUnloadChosen(false)
  }

  const tryOrder = async (data: OrderAddFormValues | undefined) => {
    try {
      if (data == undefined) return

      const request: IOrderCreate = {
        //Преобразуем дату из формы, чтобы игнорировать таймзону
        date: dayjs(data.date).format("YYYY-MM-DD"),
        deliveryType: data.deliveryType,
        delivery: {
          deliveryAddressId: data.deliveryAddressId ?? 0,
          pump: data.pump,
          commentary: data.deliveryCommentary
        },
        depotId: data.depotId,
        exchangeType: data.exchangeType,
        customerId: data.customerId,
        deliveryPrice: data?.deliveryCost ?? 0,
        wishTimeSlot: data.wishPeriodTime,
        isReserve: data.isReserve,
        transports: data.transports.map((t: TransportAddFormValues) => ({
          driverId: t.driverId,
          truckId: t.truckId,
          pickupId: t.pickupId,
          wishTimeSlot: t.wishTimeSlot,
          fuelAmount: t.fuelAmount
        })),
        fuelQuoteOrders:  data.fuelQuotes.filter((fq) => !fq.isFuture)?.map((fq: FuelQuoteAddFormValues) => ({
            id: fq.quoteId,
            amount: Number(fq.amount),
            limitSettingId: fq.limitSettingId == 0 ? null : fq.limitSettingId
          })),
        fuelFutureOrders: data.fuelQuotes.filter((fq) => fq.isFuture)?.map((fq: FuelQuoteAddFormValues) => ({
            id: fq.id,
            amount: Number(fq.amount),
            avgPrice: fq.price,
            addon: fq.addon,
            limitSettingId: fq.limitSettingId == 0 ? null : fq.limitSettingId
          })),
        newAddresses: [],
        newDrivers: newDrivers,
        newTransports: newTransports
      }

      setIsProcessing(true)
      
      if (id) {
        // if (order?.response && order.response.status == OrderStatus.NotSettedTime) {
        //   await recreateMutation.mutateAsync({
        //     ...request,
        //     id
        //   })
        // }
        // else {
          if (isOrderOnClientApprovingTransportStatus && false) { //isEditByCustomer){
            await updateTransportForOrderMutation.mutateAsync({
              ...request,
              id,
              consistChanged: isChangedOrderConsist
            })
          }
          else if (isFullUpdate) { // && !isEditByCustomer){
            await updateMutation.mutateAsync({
              ...request,
              id,
              consistChanged: isChangedOrderConsist
            })
          }
          // else if (isEditByCustomer) {
          //   await updateByCustomerMutation.mutateAsync({
          //     ...request,
          //     id,
          //     consistChanged: isChangedOrderConsist
          //   })
          // }
          else {
            await recreateMutation.mutateAsync({
              ...request,
              id
            })
          }
        // }
      } else {
        await createMutation.mutateAsync(request)
      }

      onCloseHandler()
    } catch {
    } finally {
      setIsProcessing(false)
    }
  }

  const handleOk = async () => {
    setIsModalOpen(false)
    await tryOrder(data)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  const onFinish = async (data: OrderAddFormValues) => {
    const fuelQuotes: FuelQuoteAddFormValues[] = form.getFieldValue("fuelQuotes") ?? []
    const orderTransports: TransportAddFormValues[] = form.getFieldValue("transports") ?? []

    const wishTimeSlot = form.getFieldValue("wishTimeSlot")
      if (wishTimeSlot) {
       data.wishPeriodTime = wishTimeSlot
      }
      else {
        data.wishPeriodTime = timeUnloadFuels.find(t => t.id == form.getFieldValue("wishTimeSlotId"))?.slot
      }
    data.deliveryType = isDelivery ? DeliveryType.Delivery : DeliveryType.Pickup
    data.fuelQuotes = fuelQuotes
    data.transports = orderTransports
    data.isReserve = isReserve
    setData(data)

    await tryOrder(data)
  }

  const onCloseHandler = () => {
    form.resetFields()
    setActiveKey("1")
    setId(0)
    setCustomer(undefined)
    setPrice(0)
    setFullPrice(0)
    setFuel(undefined)
    setHasWishTime(false)
    setChosenTransports([])
    setChosenFuels([])
    setChosenDepot(undefined)
    setIsDriverChosen(false)
    setIsTruckChosen(false)
    setIsTimeUnloadChosen(false)
    setIsReserve(false)
    setIsEditByCustomer(false)
    setIsFullUpdate(false)
    setIsPlanned(false)
    setIsOpen(false)
    setHasWishTime(false)
    setIsFuelSelect(false)
    setIsTransportSelect(false)
    setTimeUnloadFuels([])
    setIsAddFuelProcess(false)
    setOrderDate(undefined)
    setIsFuelLoaded(false)
    setIsDelivery(false)
    setIsPickup(false)
    setCloneId(0)
  }

  const onDateChange = (e: any, dateString: string | string[]) => {
    setOrderDate(new Date(dayjs(form.getFieldValue("date")).format("YYYY-MM-DD")))   
    if (hasWishTime && !isDelivery) {
      timeUnloadFuelLoad()
    }
    fuelLoad()
  }

  const timeUnloadChange = () => {
    const timeUnloadId = form.getFieldValue("wishTimeSlotId")
    if (timeUnloadId) {
      setIsTimeUnloadChosen(true)
      setChosenTimeUnloadFuel(timeUnloadFuels.find(t => t.id == timeUnloadId))
    }
  }

  const onAddTransportInOrder = async () => {
    const driverId = form.getFieldValue("driverId")
    const truckId = form.getFieldValue("truckId")
    const pickupId = form.getFieldValue("pickupId")
    const timeUnloadId = form.getFieldValue("wishTimeSlotId")
    const fuelAmoutTransport = form.getFieldValue("fuelAmoutTransport")

    const orderTransports: TransportAddFormValues[] = form.getFieldValue("transports") ?? []

    if (
      orderTransports.some(
        (ot) =>
          ot.driverId == driverId ||
          ot.truckId == truckId ||
          (pickupId != undefined && ot.pickupId == pickupId)
      )
    ) {
      return
    }

    orderTransports.push({
      driverId: driverId,
      truckId: truckId,
      pickupId: pickupId,
      wishTimeSlot: timeUnloadFuels.find(tuf => tuf.id == timeUnloadId)?.slot,
      fuelAmount: fuelAmoutTransport
    })

    setIsTransportSelect(true)
    refreshChosenTransports(orderTransports)
    form.setFieldValue("transports", orderTransports)
    form.setFieldValue("wishTimeSlot", timeUnloadFuels.find(f => f.id == timeUnloadId)?.slot)
    form.setFieldValue("wishTimeSlotId",undefined)
    form.setFieldValue("driverId", undefined)
    form.setFieldValue("truckId", undefined)
    form.setFieldValue("pickupId", undefined)
    form.setFieldValue("fuelAmoutTransport", undefined)
    
    setIsDriverChosen(false)
    setIsTruckChosen(false)
  }

  const onDeleteTransport = (transport: ITranportPairOrder) => {
    let orderTransports: TransportAddFormValues[] =
      form.getFieldValue("transports") ?? []
    orderTransports = orderTransports.filter((ot) => ot.driverId != transport.id)

    refreshChosenTransports(orderTransports)
    form.setFieldValue("transports", orderTransports)
    form.setFieldValue("wishTimeSlot", undefined)
    form.setFieldValue("wishTimeSlotId",undefined)
    setIsTransportSelect(false)
    setIsTimeUnloadChosen(false)
  }

  const onDepotChange = (isFuelTypeSet?: boolean) => {
    if (depots) {
      const depot = depots.response.find(
        (d) => d.id == form.getFieldValue("depotId")
      )

      if (depot) {
        uploadFuelTypes(depot.id, isFuelTypeSet)
        setHasWishTime(depot.hasWishTime)
      }

      if (depot?.hasWishTime) {
        setHasWishTime(true)
        timeUnloadFuelLoad()
      }
      else {
        setHasWishTime(false)
      }
    }
    setIsChangedOrderConsist(id > 0)
  }

  const uploadFuelTypes = (depotId: number, isFuelTypeSet?: boolean) => {
    if (depotId) {
      getNotArchivedFuelTypesByDepot(depotId).then(res => {
        setFuelTypes(res.response ?? null)
        setIsFuelTypesLoaded(true)
        setFuel(undefined)
        setIsFuelLoaded(false)
        if (!isFuelTypeSet) {
          form.resetFields(["fuelTypeId"])
        }
      })
    }
  }

  const calculatePrice = (amount: number) => {
    if (amount && amount > 0) {
      const newPrice = amount * fuelPrice
      setPrice(newPrice)
    }
    else {
      setPrice(0)
      setAmountFuel(amount ?? 0)
    }
  }

  const timeUnloadFuelLoad = async () => {
    const dateForm = form.getFieldValue("date")
    if (dateForm) {
      setTimeUnloadFuels([])
      try {
        const res = await getAvailableTimeUnloadFuelsMutation.mutateAsync({
          date: new Date(dayjs(dateForm).format("YYYY-MM-DD"))
        })
        if (res.succeeded) {
          setTimeUnloadFuels(res.response)
        }
      } catch (e) {
        console.warn(e)
        setTimeUnloadFuels([])
      }
    }
  }

  const fuelLoad = () => {
    const depotId = form.getFieldValue("depotId")
    const date = form.getFieldValue("date")
    const fuelTypeId = form.getFieldValue("fuelTypeId")
    
    if (depotId && date && fuelTypeId) {
      getFuelForOrder({
        Date: new Date(dayjs(date).format("YYYY-MM-DD")),
        FuelTypeId : fuelTypeId,
        DepotId : depotId,
        CustomerId: form.getFieldValue("customerId")
      }).then(res => {
        const fuelQuote = order?.response.fuelQuotes.find(fq => fq.id === res.response.id)

        if (fuelQuote)
          res.response.limitAvailable += fuelQuote.amount

        setFuel(res.response)
        setIsFuelLoaded(true)
      })
    }
  }
  
  const fuelTypeChange = () => {

    form.setFieldValue("volume", null)
    form.setFieldValue("weight", null)
    calculatePrice(0)

    const fuelTypeId = form.getFieldValue("fuelTypeId")

    const fuelQuotes: FuelQuoteAddFormValues[] = form.getFieldValue("fuelQuotes") ?? []
    const fuelInOrder = fuelQuotes.find((fq) => fq.fuelType == fuelTypes?.find(ft => ft.id == fuelTypeId)?.name)

    if (fuelInOrder) {
      setCannotAddFuel(true)
      form.setFieldValue("density", undefined)
      form.setFieldValue("price", undefined)
      setIsFuelLoaded(false)
      setIsAddFuelProcess(true)
      simpleModalComponent.current?.open({
        title: "Предупреждение",
        text: "Выбранный тип топлива уже добавлен. Обновить данные по топливу?",
        ok: {
          text: "Обновить",
          action: () => {
            fuelLoad()
            simpleModalComponent.current?.close()
          }
        },
        cancel: {
          text: "Выбрать другой тип топлива",
          action: () => {
            simpleModalComponent.current?.close()
          }
        }
      })
    }
    else {
      fuelLoad()
    }
    setIsChangedOrderConsist(id > 0)
  }
  
  const onWeightOrVolumeChange = (valueTypeChange: InputValueOrWeight) => {
    var amountLitres: number = form.getFieldValue("volume")
    var amountTonn: number = form.getFieldValue("weight")

    switch (valueTypeChange) {
      case InputValueOrWeight.volume:
        if (amountLitres && !Number.isInteger(amountLitres)) {
          amountLitres = Math.floor(Number(amountLitres))
          form.setFieldValue("volume", amountLitres)
        }

        amountTonn = convertFuelAmountToTonn(amountLitres, fuel?.density ?? 1)
        form.setFieldValue("weight", amountTonn.toFixed(3))
        setAmountFuel(amountTonn)
        break

      case InputValueOrWeight.weight:
        amountLitres = convertFuelAmountToLitres(amountTonn, fuel?.density ?? 1)
        if (amountLitres && !Number.isInteger(amountLitres)) {
          amountLitres = Math.floor(Number(amountLitres))
        }
        form.setFieldValue("volume", amountLitres)
        setAmountFuel(amountTonn)
        break
    }
    calculatePrice(amountTonn)
  }

  const onFormContinueOrder = async () => {
    try {
      await form.validateFields()
      
      form.setFieldValue("deliveryType", DeliveryType.Pickup)

      setChosenDeliveryType(DeliveryType.Pickup)

      const fuels: FuelQuoteAddFormValues[] =
        form.getFieldValue("fuelQuotes") ?? []
      const orderTransports: TransportAddFormValues[] =
        form.getFieldValue("transports") ?? []

      const totalAmount = fuels.reduce((sum, f) => sum + f.amount, 0)
      const deliveryCost = deliveryAddres?.deliveryCost && deliveryAddres.deliveryCost > 0 ?
        deliveryAddres?.deliveryCost * totalAmount
        : deliveryAddres?.defaultDeliveryCost ?? 0
  
      const fullPrice = fuels.reduce((sum, a) => sum + (Number(a.price) * a.amount), 0) + (+deliveryCost)
      setTotalAmount(totalAmount)

      setOrderData({
        date: formatDate(form.getFieldValue("date").toString()),
        customer: {
          id: customer ? customer.id : form.getFieldValue("customerId"),
          name: customer
            ? customer.name
            : customers?.find((c) => c.value == form.getFieldValue("customerId"))
              ?.label ?? "-"
        },
        isReserve: isReserve,
        wishPeriodTime: isTimeUnloadChosen 
        ? timeUnloadFuels.find(t => t.id == form.getFieldValue("wishTimeSlotId"))?.slot : "-",
        deliveryType:
          deliveryTypes.find(
            (d) => d.type == form.getFieldValue("deliveryType")
          )?.name ?? "Не выбран",
        delivery: isDelivery ? 
        {
          deliveryAddress: deliveryAddres,
          commentary: form.getFieldValue("deliveryCommentary")
        } : undefined ,
        exchangeType:
          chosenDeliveryType != DeliveryType.Delivery
            ? exchangeTypes.find(
              (e) => e.type == form.getFieldValue("exchangeType")
            )?.name ?? "Не выбран"
            : undefined,
        depot:
          depots?.response.find((d) => d.id == form.getFieldValue("depotId"))
            ?.name ?? "Не выбрана",
        depotId: form.getFieldValue("depotId"),
        transports:
          chosenDeliveryType === DeliveryType.Pickup
            ? orderTransports.map((t) => ({
              id: t.driverId,
              driverId: t.driverId,
              driver: allDrivers.find((d) => d.id == t.driverId),
              truckId: t.truckId,
              truck: allTrucks.find((tr) => tr.id == t.truckId),
              pickupId: t.pickupId,
              pickup: allPickups.find((p) => p.id == t.pickupId),
              wishTimeSlot: t.wishTimeSlot,
              fuelAmount: t.fuelAmount 
            }))
            : [],
        fuels: fuels.map((fq) => ({
          id: fq.id,
          quoteId: fq.id,
          amount: fq.amount,
          fuelTypeId: fq.fuelTypeId,
          fuelType: fq.fuelType,
          density: fq.density,
          price: Number(fq.price),
          addon: fuel?.addon ?? 0,
          isFuture: fq.isFuture,
          limitSettingId: fq.limitSettingId
        })),
        deliveryPrice: deliveryCost,
        fullPrice: fullPrice,
        finalPriceText: `Общая сумма: ${getMoney(fullPrice)}`
      })
      setActiveKey("2")
    } catch (errorInfo) { }
  }

  const onAddFuelInOrder = () => {
    if (fuel && amountFuel) {
      const amount = amountFuel
      const fuelQuotes: FuelQuoteAddFormValues[] = form.getFieldValue("fuelQuotes") ?? []
      let fuelInOrder = fuelQuotes.find((fq) => fq.id == fuel.id)

      if (fuelInOrder) {
        fuelInOrder.amount = Number(amount)
        fuelInOrder.price = fuelPrice
        setTotalAmount(fuelQuotes.reduce((sum, f) => sum + f.amount, 0))
      } 
      else {
        const fuelType = fuelTypes?.find(ft => ft.id == form.getFieldValue("fuelTypeId")) 

        fuelInOrder = {
          id: fuel.id,
          quoteId: fuel.quoteId,
          amount: amount,
          fuelTypeId: fuelType!.id,
          fuelType: fuelType!.name,
          density: fuel.density,
          price: fuelPrice,
          addon: fuel.addon,
          isFuture: fuel.isFuture,
          limitSettingId: fuel.limitSettingId ?? 0
        }

        setTotalAmount(prev => prev + amount)
        fuelQuotes.push(fuelInOrder)
      }

      setFullPrice(fuelQuotes.reduce((sum, quote) => sum + (quote.price * quote.amount), 0))

      if (fuel && !fuel.limit) {
        setIsReserve(true)
        form.setFieldValue("isReserve", true)
      }
      else{
        setIsReserve(false)
        form.setFieldValue("isReserve", false)
      }

      refershChosenFuels(fuelQuotes)
      setIsFuelSelect(true)
      form.setFieldValue("fuelQuotes", fuelQuotes)

      setAmountFuel(0)
      setCannotAddFuel(true)
      form.setFieldValue("fuelTypeId", undefined)
      form.setFieldValue("amount",undefined)
      form.setFieldValue("volume",undefined)
      form.setFieldValue("weight",undefined)
      form.setFieldValue("density", undefined)
      form.setFieldValue("price", undefined)
      setIsFuelLoaded(false)
      setIsAddFuelProcess(false)
      setFuel(undefined)
      setPrice(0)
    }
  }

  const onAddFuelType  = () => {
    if (isAddFuelProcess) {
      setIsAddFuelProcess(false)
      setIsFuelLoaded(false)
      setIsFuelSelect(true)
    }
    else{
      setIsAddFuelProcess(true)
      setIsFuelSelect(false)
    }
    setIsChangedOrderConsist(id > 0)
  }

  const onDeleteFuel = (key: number) => {
    let fuelQuotes: FuelQuoteAddFormValues[] = form.getFieldValue("fuelQuotes") ?? []
    const deleteFuel = chosenFuels.find(f => f.id == key)
    setFullPrice(fullPrice-(deleteFuel!.price * deleteFuel!.amount))
    fuelQuotes = fuelQuotes.filter((fq) => fq.id != key)

    setTotalAmount(prev => prev - (deleteFuel?.amount ?? 0))
    refershChosenFuels(fuelQuotes)
    form.setFieldValue("fuelQuotes", fuelQuotes)
    setIsAddFuelProcess(false)
    setPrice(0)
    setFuel(undefined)
    if (fuelQuotes.length > 0) {
      setIsFuelSelect(true)
    }
    else {
      setIsFuelSelect(false)
    }
    fuelTypeChange()
    setIsChangedOrderConsist(id > 0)

    if(isBulkOrder){
      form.setFieldValue("fuelTypeId", deleteFuel?.fuelTypeId)
      fuelTypeChange()
    }
  }

  const refreshChosenTransports = (ts: TransportAddFormValues[]) => {
    if (allDrivers.length == 0 || !transports?.response) {
      return
    }

    setChosenTransports(
      ts.map((t) => ({
        id: t.driverId,
        driverId: t.driverId,
        driver: allDrivers.find((d) => d.id == t.driverId),
        truckId: t.truckId,
        truck: allTrucks.find((tr) => tr.id == t.truckId),
        pickupId: t.pickupId,
        pickup: allPickups.find((p) => p.id == t.pickupId),
        wishTimeSlot: t.wishTimeSlot,
        fuelAmount: t.fuelAmount
      }))
    )
  }

  const refershChosenFuels = (fuelQuotes: FuelQuoteAddFormValues[]) => {
    setChosenFuels(
      fuelQuotes.map((fq) => ({
        id: fq.id,
        quoteId: fq.id,
        amount: fq.amount,
        fuelTypeId: fq.fuelTypeId,
        fuelType: fq.fuelType,
        density: fq.density,
        price: Number(fq.price),
        addon: fq.addon,
        isFuture: fq.isFuture,
        limitSettingId: fq.limitSettingId
      }))
    )

    setChosenDepot(
      depots?.response.find((d) => d.id == form.getFieldValue("depotId"))
        ?.name ?? undefined
    )
  }

  const onCreateOrder = () => {
    form.submit() 
  }

  const additionalTransportColumns = [
    {
      title: "",
      dataIndex: "delete",
      render: (_: any, transport: ITranportPairOrder) =>
        activeKey == "1" ? (
          <Button
            danger
            type="primary"
            onClick={() => onDeleteTransport(transport)}
          >
            X
          </Button>
        ) : null
    }
  ]

  const additionalFuelColumns = [
    {
      title: "",
      dataIndex: "delete",
      render: (_: any, fuel: IOrderFuel) =>
        activeKey == "1" ? (
          <Button danger type="primary" disabled = {isOrderOnClientApprovingTransportStatus} onClick={() => onDeleteFuel(fuel.id)}>
            X
          </Button>
        ) : null
    }
  ]

  const disabledDate: AntDatePickerProps["disabledDate"] = (current) => {
    return current < dayjs().add(-1, "day")
  }

  const onReturnToChangeOrderBtn = () => {
    setActiveKey("1")
    setFuel(undefined)
  }

  const tabsItems: TabsProps["items"] = [
    {
      key: "1",
      label: "Оформление заказа",
      children:
        <>
          <DeliveryAddressesAddForm 
            open={() => addressComponent.current?.open()}
            close={() => addressComponent.current?.close()} 
            onCloseAction={() => deliveryForm.current?.refetch() }
            ref={addressComponent} 
          />
          <Row>
            <Col>
              <Form.Item
                hidden={ true }
                name="deliveryType"
              >
                <Input value={ isDelivery ? DeliveryType.Delivery : DeliveryType.Pickup } />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                hidden={ true }
                name="isReserve"
                valuePropName="checked"
              >
                <Checkbox value={ isReserve } />
              </Form.Item>
            </Col>
          </Row>
           <Row hidden={isCustomerOrder} gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="customerId"
                  label="Контрагент"
                  rules={[
                    {
                      required: !isCustomerOrder,
                      message: "Пожалуйста выберите контрагент"
                    }
                  ]}
                >
                  <Select
                    disabled={id != 0}
                    placeholder="Контрагент"
                    onChange={onCustomerChange}
                    showSearch
                    filterOption={(inputValue, option) =>
                        filterSelectOption(inputValue, option?.label || "")
                    }
                    options={customers}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row
              hidden={customer == undefined || id == 0}
              className={classes["order-row"]}
              gutter={16}
            >
              <Col xs={24} md={12}>
                <Row gutter={[12, 6]}>
                  <Col span={24}>
                    <Text type="secondary">Заказчик:</Text>
                  </Col>
                  <Col span={24}>
                    <Text className={classes["f16"]}>{customer?.name}</Text>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item 
                  name={"depotId"}
                  label= "Нефтебаза"
                  rules={[
                    {
                      required : true,
                      message : "Пожалуйста выберите нефтебазу"
                    }
                  ]}>
                    <Select 
                      disabled= {isFuelSelect || isAddFuelProcess || isBulkOrder}
                      placeholder = "Нефтебаза"
                      showSearch
                      onChange={() => { onDepotChange()}}
                      filterOption={(inputValue, option) =>
                        filterSelectOption(
                            inputValue,
                            option?.label || ""
                        )
                      }
                      options={depots?.response.map((depot) => ({
                        value: depot.id,
                        label: depot.name
                      }))}
                    />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="date"
                  label="Дата"
                  rules={[
                    { required: true, message: "Пожалуйста выберите дату" }
                  ]}
                >
                  <DatePicker
                    disabled= {isFuelSelect || isAddFuelProcess || isBulkOrder}
                    style={{ width: "100%" }}
                    placeholder="Дата заказа"
                    disabledDate={disabledDate}
                    onChange={onDateChange}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="fuelTypeId"
                  label="Тип топлива"
                >
                  <Select
                    disabled= {isFuelSelect && !isAddFuelProcess || isBulkOrder}
                    onChange={fuelTypeChange}
                    placeholder="Тип топлива"
                    showSearch
                    filterOption={(inputValue, option) =>
                      filterSelectOption(
                        inputValue,
                        option?.label || ""
                      )
                    }
                    options={fuelTypes?.map((fuelType) => ({
                      value: fuelType.id,
                      label: `${fuelType.name}`
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  hidden={isDelivery}
                  name="exchangeType"
                  label="Вид обменной доверенности"
                  rules={[
                    {
                      required: !isDelivery,
                      message:
                          "Пожалуйста выберите Вид обменной доверенности"
                    }
                  ]}
                >
                  <Select
                    disabled={isDelivery}
                    placeholder="Вид обменной доверенности"
                    showSearch
                    filterOption={(inputValue, option) =>
                        filterSelectOption(inputValue, option?.label || "")
                    }
                    options={exchangeTypes.map((type) => ({
                      value: type.type,
                      label: type.name
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            {(chosenFuels && chosenFuels.length > 0) || isFuelLoaded ? (
              <>
                <Row gutter={16} hidden={!isFuelLoaded}>
                  <Col xs={22}>
                    {(fuel && (fuel.price > 0 || isBulkOrder)
                      ? ` ${getMoney(fuelPrice)} за 1 тонну`
                      : `Цена не установлена, последнее установленное значение: 
                        ${getMoney(fuel?.lastSetPrice ?? 0)} за 1 тонн`)}
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Объем, л." name="volume">
                      <InputNumber
                        className={classes["inputNumber"]}
                        onChange={() => onWeightOrVolumeChange(InputValueOrWeight.volume)}
                        type="number"
                        placeholder="Объем"
                        min={0}
                        step={1}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Item label="Масса, т." name="weight">
                      <InputNumber
                        className={classes["inputNumber"]}
                        onChange={(e) => onWeightOrVolumeChange(InputValueOrWeight.weight)}
                        type="number"
                        placeholder="Масса"
                        min={0}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={12} md={6} style={{ display: "flex", alignItems: "flex-end" }}>
                    <Button
                      disabled={price === 0 }
                      className={classes["align-center"]}
                      type="primary"
                      size="large"
                      shape="round"
                      onClick={onAddFuelInOrder}
                    >
                      Добавить{price > 0 ? ` (${getMoney(price)})` : ""}
                    </Button>
                  </Col>
                </Row>
                <div
                  hidden={!chosenFuels || chosenFuels.length == 0}
                  className={classes["order-row"]}
                >
                  <OrderFuelTable
                    additionalColumns={additionalFuelColumns}
                    fuels={chosenFuels}
                  />
                </div>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} className={classes["col-flex"]}>
                    <Text strong>Итого: {getMoney(fullPrice)} </Text>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12} className={classes["col-flex"]}>
                    <Button
                      hidden={!chosenFuels || chosenFuels.length == 0}
                      className={classes["add-fuel-btn"]}
                      type="primary"
                      size="large"
                      shape="round"
                      onClick={onAddFuelType}
                    >
                      {isAddFuelProcess ? "отменить добавление" : "добавить топливо"}
                    </Button>
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  {fuel && fuelPrice < 1  && (
                    <Col span={24}>
                      <Alert
                        message="На выбранную дату не установлена цена на топливо. Расчет будет произведен по последней установленной цене"
                        type="warning" showIcon closable
                      />
                    </Col>
                  )}
                  {fuel && !fuel.limit && !isBulkOrder && (
                    <Col span={24}>
                      <Alert
                        message="Лимит топлива на выбранную дату не установлен, заявка будет оформлена как резервирование топлива"
                        type="warning" showIcon closable
                      />
                    </Col>
                  )}
                  { fuel && fuel.limit && fuel.limitAvailable < amountFuel && (
                    <Col span={24}>
                      <Alert
                        message="Указанный объем превышает доступный лимит. Заявка будет передана в обработку администратору"
                        type="warning" showIcon closable
                      />
                    </Col>
                  )}
                  {customer && customer.availableSum < price && (
                    <Col span={24}>
                      <Alert
                        message="Недостаточно средств, доступных для заказа. Заявка будет иметь меньший приоритет при обработке"
                        type="warning" showIcon closable
                      />
                    </Col>
                  )}
                </Row>
              </>
              ) : <Alert message={cloneId === 0 ? "Выберите нефтебазу, тип топлива и дату" : "Выберите дату"} type="warning" showIcon closable />
            }

            {id > 0 && isChangedOrderConsist && (
              <Alert
                message="Внимание! После изменения состава заказа данные об оплате будут аннулированы, а заказ - пересчитан."
                type="error" showIcon
              />
            )}

            <Row gutter={16} className={classes["order-row-balance"]}>
              <Col xs={24} md={12}>
                <Text strong> Сумма доступная для заказа: { getMoney(customer?.availableSum ?? 0) } </Text>
              </Col>
            </Row>
            <div>
              {isDelivery && (
                <Collapse 
                  className={classes["collapse-order"]}
                  ghost
                  defaultActiveKey={["1"]}
                  items={[
                    { key: 1,
                      label:"Доставка",
                      children: (
                        <>
                          {
                            <DeliveryForm
                              ref={deliveryForm}
                              initialValue={order ? 
                                { 
                                  delivery: order?.response.delivery, 
                                  deliveryCost: order?.response.delivery.deliveryAddress?.deliveryCost } : undefined}
                              addDeliveryAddresComponent={addressComponent}
                              form={form}
                              isActive ={isDelivery} 
                              customerId={customer?.id ?? 0}
                              fuelsTotalAmount={totalAmount}
                              setAddress={(addres: IDeliveryAddress) => setDeliveryAddres(addres)}
                            />
                          }
                        </>
                      )
                    }
                  ]}
                />
              )}
            {!!(!isDelivery || (id && !isEditByCustomer)) && (
              <div className={classes["order-row"]}>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item 
                    name="driverId"
                    label="Водитель"
                    rules={[
                      {
                        required: hasWishTime && chosenTransports && chosenTransports.length == 0,
                        message: "Пожалуйста выберите водителя"
                      }
                    ]}>
                      <Select
                        disabled = {isTransportSelect}
                        onChange={(e: number) =>
                            setIsDriverChosen(e != undefined)
                        }
                        placeholder="Водитель"
                        showSearch
                        allowClear={true}
                        filterOption={(inputValue, option) =>
                            filterSelectOption(
                                inputValue,
                                option?.label || ""
                            )
                        }
                        options={allDrivers
                            .filter(
                                (d) =>
                                    !chosenTransports.some(
                                        (ct) => ct.driverId == d.id
                                    )
                            )
                            .map((driver) => ({
                              value: driver.id,
                              label: driver.fullName
                            }))}
                        dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                Нет водителя в списке? Добавьте в справочнике водителей!
                              </Space>
                            </>
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item 
                      name="truckId"
                      label="Транспорт"
                      rules={[
                        {
                          required: hasWishTime && chosenTransports && chosenTransports.length == 0,
                          message: "Пожалуйста выберите транспорт"
                        }
                      ]}
                    >
                      <Select
                        disabled = {isTransportSelect}
                        onChange={(e: number) => setIsTruckChosen(e != undefined) }
                        placeholder="Транспорт"
                        showSearch
                        allowClear={true}
                        filterOption={(inputValue, option) =>
                            filterSelectOption(
                                inputValue,
                                option?.label || ""
                            )
                        }
                        options={allTrucks
                            .filter(
                                (tr) =>
                                    !chosenTransports.some(
                                        (ct) => ct.truckId == tr.id
                                    )
                            )
                            .map((transport) => ({
                              value: transport.id,
                              label: formatTransport(transport)
                            }))}
                        dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                Нет транспорта в списке? Добавьте в справочнике транспорта!
                              </Space>
                            </>
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="pickupId" label="Прицеп">
                      <Select
                        disabled = {isTransportSelect}
                        placeholder="Прицеп"
                        showSearch
                        allowClear={true}
                        filterOption={(inputValue, option) =>
                            filterSelectOption(
                                inputValue,
                                option?.label || ""
                            )
                        }
                        options={allPickups
                            .filter(
                                (p) =>
                                    !chosenTransports.some(
                                        (ct) => ct.pickupId == p.id
                                    )
                            )
                            .map((transport) => ({
                              value: transport.id,
                              label: formatTransport(transport)
                            }))}
                        dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                Нет прицепа в списке? Добавьте в справочнике транспорта!
                              </Space>
                            </>
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      hidden={!hasWishTime}
                      name="wishTimeSlotId"
                      label="Желаемый период загрузки"
                      rules={[
                        {
                          required : hasWishTime && chosenTransports && chosenTransports.length == 0 ,
                          message : "Пожалуйста выберите время отгрузки"
                        }
                      ]}
                    >
                      <Select
                        disabled= {isTransportSelect}
                        placeholder="Желаемый период загрузки"
                        showSearch
                        allowClear={true}
                        onChange={timeUnloadChange}
                        filterOption={(inputValue, option) =>
                          filterSelectOption(
                            inputValue,
                            option?.label || "")}
                        options={timeUnloadFuels.map((timeUnload) => ({
                          value: timeUnload.id,
                          label: `${timeUnload.slot}`
                        }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col
                    xs={24}
                    md={12}
                    className={classes["col-flex"]}
                  >
                    <Button
                      disabled={ (chosenTransports.length == 1 && chosenFuels.length > 0) || !isDriverChosen || !isTruckChosen
                        || (hasWishTime && !isTimeUnloadChosen)}
                      className={classes["align-center"]}
                      type="primary"
                      size="large"
                      shape="round"
                      onClick={onAddTransportInOrder}
                    >
                      Добавить
                    </Button>
                  </Col>
                </Row>

                <div
                  hidden={chosenTransports.length == 0}
                  className={classes["order-row"]}
                >
                  <TransportTable
                    orderDate={orderDate}
                    additionalColumns={additionalTransportColumns}
                    transports={chosenTransports}
                  />
                </div>
              </div>
            )}
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Button
                  disabled={ (hasWishTime && chosenTransports.length == 0 && !isDelivery) || chosenFuels.length == 0 }
                  type="primary"
                  size="large"
                  shape="round"
                  onClick={onFormContinueOrder}
                >
                  {formOrderText}
                </Button>
              </Col>
            </Row>
          </>,
      icon: <ProfileOutlined />
    },
    {
      key: "2",
      label: "Подтверждение заказа",
      children:
        <>
          <Row className={classes["order-row"]} gutter={16} >
            <Col xs={24} md={12}>
              <Row gutter={[12, 6]}>
                <Col span={24}>
                  <Text type="secondary">Заказчик:</Text>
                </Col>
                <Col span={24}>
                  <Text className={classes["f16"]}>
                    {orderData?.customer.name}
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className={classes["order-row"]} gutter={16} >
            <Col xs={24} md={12}>
              <Row gutter={[12, 6]}>
                <Col span={24}>
                  <Text type="secondary">Дата:</Text>
                </Col>
                <Col span={24}>
                  <Text className={classes["f16"]}>{orderData?.date}</Text>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className={classes["order-row"]} gutter={16} >
            <Col hidden={orderData?.delivery != undefined} xs={24} md={12}>
              <Row gutter={[12, 6]}>
                <Col span={24}>
                  <Text type="secondary">Вид обменной доверенности:</Text>
                </Col>
                <Col span={24}>
                  <Text className={classes["f16"]}>
                    {orderData?.exchangeType}
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>

          <div>
          <Collapse
            className={classes["collapse-order"]}
            defaultActiveKey={["1"]}
            ghost
            items={[
              {
                key: "1",
                label: "Выбор топлива",
                children: (
                    <>
                      <Row className={classes["order-row"]} gutter={16}>
                        <Col span={24}>
                          <Row gutter={[12, 6]}>
                            <Col span={24}>
                              <Text type="secondary">Нефтебаза:</Text>
                            </Col>
                            <Col span={24}>
                              <Text className={classes["f16"]}>
                                {orderData?.depot}
                              </Text>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <div
                          hidden={
                              orderData?.fuels && orderData?.fuels.length == 0
                          }
                          className={classes["order-row"]}
                      >
                        <OrderFuelTable fuels={orderData?.fuels ?? []} />
                      </div>
                    </>
                )
              }
            ]}
          ></Collapse>
            {!isDelivery && (
              <Collapse
                className={classes["collapse-order"]}
                defaultActiveKey={["1"]}
                ghost
                items={[
                  {
                    key: "1",
                    label: "Выбор транспорта",
                    children: (
                      <>
                        <div
                          hidden={
                              orderData?.transports &&
                              orderData?.transports.length == 0
                          }
                          className={classes["order-row"]}
                        >
                          <TransportTable
                            orderDate={orderDate}
                            transports={orderData?.transports ?? []}
                          />
                        </div>
                        <Text
                          hidden={
                              !orderData?.transports ||
                              orderData?.transports.length > 0
                          }
                        >
                          Не выбрано
                        </Text>
                      </>
                    )
                  }
                ]}
              />
            )}
            {isDelivery && (
              <Collapse
                className={classes["collapse-order"]}
                defaultActiveKey={["1"]}
                ghost
                items={[
                  {
                    key: "1",
                    label: "Доставка",
                    children: (
                      <>
                        <Row className={classes["order-row"]} gutter={16}>
                          <Col span={24}>
                            <OrderDeliveryTable
                              deliveryDefaultPrice={(orderData?.delivery?.deliveryAddress?.defaultDeliveryCost ?? 0) * totalAmount}
                              delivery={orderData?.delivery}
                              showPrice={true}
                              deliveryPrice={(orderData?.delivery?.deliveryAddress?.deliveryCost ?? 0) * totalAmount}
                            />
                          </Col>
                        </Row>
                      </>
                    )
                  }
                ]}
              />
            )}
          </div>
          <Row
            className={classes["order-row"]}
            gutter={16}
          >
            <Col xs={24} md={12}>
              <Text className={classes["f16"]}>
                {orderData?.finalPriceText}
              </Text>
            </Col>
          </Row>
          <Flex className={classes["order-row"]} gap="middle">
            <Button
              type="primary"
              size="large"
              shape="round"
              loading={isProcessing}
              onClick={onCreateOrder}
            >
              {orderText}
            </Button>
            <Button
              type="primary"
              ghost
              size="large"
              shape="round"
              onClick={onReturnToChangeOrderBtn}
            >
              Изменить
            </Button>
          </Flex>
        </>,
      icon: <IdcardOutlined />
    }
  ]

  if (hasPermission(Resource.Order,Scope.AllActions) && id == 0){
    tabsItems.push({
      key: "3",
      label: "Создать заказ задним числом",
      children: <BackdatingOrderAddForm isOpen={isOpen} onClose={onCloseHandler}/> 
    })
  }

  return (
    <Drawer
      title={title}
      width={820}
      onClose={onCloseHandler}
      open={isOpen}
      maskClosable={false}
      styles={{ body: { padding: "10px 40px 80px 40px", borderRadius: "20px" } }}
    >
      {notificationContextHolder}
      {contextHolder}
      <SimpleModal ref={simpleModalComponent}></SimpleModal>
      <Modal
        title={modalTitle}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <Text>{modalText}</Text>
      </Modal>

      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Spin
          spinning={depotsIsLoading && driversIsLoading && transportsIsLoading}
        >
          {!isPickup && !isDelivery &&
            <Flex justify="center" gap="50px">
              <Flex gap={8} style={{ width: "100%", flexDirection: "column" }}>
                  <OrderButton
                    onClick={() => {
                      setIsPickup(true)
                      setIsDelivery(false)
                    }}
                    iconPath={"/order_pickup_icon.svg"}
                    mainText={"Заказать самовывоз"}
                    secondaryText={"Оформите онлайн самовывоз"}
                    backgroundColor={"#F2F6FF"}
                  />
                  <OrderButton
                    onClick={() => {
                      setIsDelivery(true)
                      setIsPickup(false)
                    }}
                    iconPath={"/order_delivery_icon.svg"}
                    mainText={"Заказать доставку"}
                    secondaryText={"Оформите онлайн доставку"}
                    backgroundColor={"#FFF9F2"}
                  />
              </Flex>
            </Flex>
          }
          
          {(isPickup || isDelivery) &&
            <Tabs
              defaultActiveKey="1"
              activeKey={activeKey}
              items={tabsItems} 
              onChange={(e) => {
                if (e == "3" || e == "1") {
                  setActiveKey(e)
                }
              }}
            />
          }
        </Spin>
      </Form>
    </Drawer>
  )
})

interface OrderButtonProps {
  onClick: () => void
  iconPath: string
  mainText: string
  secondaryText: string
  backgroundColor: string
}

const OrderButton = (props: OrderButtonProps) => {
  return (
    <Flex
      className={classes["order-button"]}
      style={{ backgroundColor: props.backgroundColor }}
      onClick={props.onClick}
    >
      <Flex className={classes["order-button_inner"]}>
        <SvgImage width={40} height={40} src={props.iconPath} styles={{ marginRight: "24px" }} />
        <div>
          <p className={classes["order-button_inner-main-text"]}>{props.mainText}</p>
          <p className={classes["order-button_inner-secondary-text"]}>{props.secondaryText}</p>
        </div>
      </Flex>
      <SvgImage width={10} height={20} src={"/order_button_arrow.svg"} />
    </Flex>
  )
}